import { Building, GAME_CONFIG } from '../constants';
import { DebugState } from '../hooks/useDebugState';
import { inputLogger } from '../Logger';

import {
  Entity,
  Position,
  Rectangle,
  CollisionResult,
  ValidationResult,
  CollisionLayer,
  EntityType,
  CollisionRule,
  BuildingEntity,
  TerrainEntity,
  QueryOptions,
} from './CollisionTypes';
import { SpatialGrid } from './SpatialGrid';

/**
 * Unified collision detection service
 * Handles all collision queries, validation, and spatial optimization
 */
export class CollisionService {
  private spatialGrid: SpatialGrid;
  private collisionRules: CollisionRule[];
  private debugState?: DebugState;
  private isDirty: boolean = true;
  private rebuildCallback?: () => void;

  constructor(gridCellSize: number = 16) {
    this.spatialGrid = new SpatialGrid(gridCellSize);
    this.collisionRules = this.initializeCollisionRules();

    inputLogger.info('CollisionService initialized', {
      gridCellSize,
      ruleCount: this.collisionRules.length,
    });
  }

  /**
   * Initialize default collision rules
   */
  private initializeCollisionRules(): CollisionRule[] {
    return [
      // Player cannot move through solid terrain
      {
        layer1: CollisionLayer.PLAYER,
        layer2: CollisionLayer.TERRAIN,
        canOverlap: false,
        condition: (_player, terrain) =>
          (terrain as TerrainEntity).terrainType !== 'grass',
        reason: 'Cannot move through solid terrain',
      },

      // Player cannot move through solid buildings
      {
        layer1: CollisionLayer.PLAYER,
        layer2: CollisionLayer.BUILDINGS,
        canOverlap: false,
        condition: (_player, building) =>
          (building as BuildingEntity).building.blocking,
        reason: 'Cannot move through large buildings',
      },

      // Player can move through belts and miners
      {
        layer1: CollisionLayer.PLAYER,
        layer2: CollisionLayer.BUILDINGS,
        canOverlap: true,
        condition: (_player, building) => {
          const b = building as BuildingEntity;
          return (
            b.building.category === 'logistics' ||
            b.building.category === 'mining'
          );
        },
      },

      // Player cannot move through solid resource nodes (iron/copper) but can walk over collectible resources (*)
      {
        layer1: CollisionLayer.PLAYER,
        layer2: CollisionLayer.RESOURCES,
        canOverlap: false,
        condition: (_player, resource) => {
          // Only block if the resource entity is solid
          return resource.isSolid;
        },
        reason: 'Cannot move through mining nodes',
      },

      // Buildings cannot overlap with other buildings
      {
        layer1: CollisionLayer.BUILDINGS,
        layer2: CollisionLayer.BUILDINGS,
        canOverlap: false,
        reason: 'Buildings cannot overlap',
      },

      // Buildings cannot be placed on solid terrain
      {
        layer1: CollisionLayer.BUILDINGS,
        layer2: CollisionLayer.TERRAIN,
        canOverlap: false,
        condition: (_building, terrain) => {
          const t = terrain as TerrainEntity;
          return ['wall', 'water', 'mountain', 'tree', 'rock'].includes(
            t.terrainType
          );
        },
        reason: 'Cannot place buildings on solid terrain',
      },

      // Miners can only be placed on resource nodes
      {
        layer1: CollisionLayer.BUILDINGS,
        layer2: CollisionLayer.RESOURCES,
        canOverlap: true,
        condition: (building, _resource) => {
          const b = building as BuildingEntity;
          return (
            b.building.category === 'mining' &&
            b.building.requiresResourceNode !== false
          );
        },
        reason: 'Only miners can be placed on resource nodes',
      },

      // Non-miners cannot be placed on resource nodes
      {
        layer1: CollisionLayer.BUILDINGS,
        layer2: CollisionLayer.RESOURCES,
        canOverlap: false,
        condition: (building, _resource) =>
          (building as BuildingEntity).building.category !== 'mining',
        reason: 'Cannot place buildings on resource nodes',
      },
    ];
  }

  /**
   * Set debug state for collision overrides
   */
  setDebugState(debugState: DebugState): void {
    this.debugState = debugState;
  }

  /**
   * Add an entity to the collision system
   */
  addEntity(entity: Entity): void {
    this.spatialGrid.insert(entity);
  }

  /**
   * Remove an entity from the collision system
   */
  removeEntity(entity: Entity): void {
    this.spatialGrid.remove(entity);
  }

  /**
   * Update an entity's position in the collision system
   */
  updateEntity(entity: Entity): void {
    this.ensureUpToDate();
    this.spatialGrid.update(entity);
  }

  /**
   * Check if a position is walkable (for player movement)
   */
  canMoveTo(x: number, y: number, entity: Entity): CollisionResult {
    this.ensureUpToDate();

    // Debug override - can move anywhere if collision is disabled
    if (this.debugState?.collisionDisabled) {
      return {
        canMove: true,
        canPlace: true,
        blockingEntities: [],
      };
    }

    // Query entities at the target position
    const entitiesAtPosition = this.spatialGrid.queryPosition(x, y, {
      layers: [
        CollisionLayer.TERRAIN,
        CollisionLayer.BUILDINGS,
        CollisionLayer.RESOURCES,
      ],
    });

    const blockingEntities: Entity[] = [];
    const reasons: string[] = [];

    // Check collision rules
    for (const otherEntity of entitiesAtPosition) {
      const rule = this.findCollisionRule(
        entity.collisionLayer,
        otherEntity.collisionLayer
      );
      if (rule && !this.canEntitiesOverlap(entity, otherEntity, rule)) {
        blockingEntities.push(otherEntity);
        if (rule.reason) {
          reasons.push(rule.reason);
        }
      }
    }

    return {
      canMove: blockingEntities.length === 0,
      canPlace: blockingEntities.length === 0,
      blockingEntities,
      reason: reasons.join(', '),
    };
  }

  /**
   * Validate building placement at a position
   */
  validateBuildingPlacement(
    building: Building,
    x: number,
    y: number
  ): ValidationResult {
    this.ensureUpToDate();

    // Debug override - can place anywhere if collision is disabled
    if (this.debugState?.collisionDisabled) {
      return {
        isValid: true,
        errors: [],
        warnings: [],
        blockingEntities: [],
        suggestedPositions: [],
      };
    }

    const errors: string[] = [];
    const warnings: string[] = [];
    const blockingEntities: Entity[] = [];

    // Get all positions this building will occupy
    const occupiedPositions = this.getBuildingPositions(building, x, y);

    // Check each position
    for (const pos of occupiedPositions) {
      const entitiesAtPosition = this.spatialGrid.queryPosition(pos.x, pos.y);

      for (const entity of entitiesAtPosition) {
        // Skip if it's the same building being updated
        if (
          entity.type === EntityType.BUILDING &&
          entity.id === `building_${x}_${y}`
        ) {
          continue;
        }

        // Create a temporary building entity for collision checking
        const tempBuildingEntity: BuildingEntity = {
          id: `temp_building_${x}_${y}`,
          type: EntityType.BUILDING,
          position: { x, y },
          bounds: {
            x,
            y,
            width: building.size?.width || 1,
            height: building.size?.height || 1,
          },
          collisionLayer: CollisionLayer.BUILDINGS,
          collisionMask: [
            CollisionLayer.TERRAIN,
            CollisionLayer.BUILDINGS,
            CollisionLayer.RESOURCES,
          ],
          isSolid: building.blocking,
          isStatic: true,
          building,
          centerPosition: { x, y },
          occupiedPositions,
        };

        const rule = this.findCollisionRule(
          tempBuildingEntity.collisionLayer,
          entity.collisionLayer
        );

        if (
          rule &&
          !this.canEntitiesOverlap(tempBuildingEntity, entity, rule)
        ) {
          blockingEntities.push(entity);
          if (rule.reason) {
            errors.push(`Position (${pos.x}, ${pos.y}): ${rule.reason}`);
          }
        }
      }
    }

    // Additional specific validations
    this.validateBuildingSpecificRules(
      building,
      x,
      y,
      errors,
      warnings,
      blockingEntities
    );

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      blockingEntities,
      suggestedPositions: [], // TODO: Implement suggestion algorithm
    };
  }

  /**
   * Get entities at a specific position
   */
  getEntitiesAt(x: number, y: number, options: QueryOptions = {}): Entity[] {
    return this.spatialGrid.queryPosition(x, y, options);
  }

  /**
   * Get entities within a radius
   */
  getEntitiesInRadius(
    center: Position,
    radius: number,
    options: QueryOptions = {}
  ): Entity[] {
    return this.spatialGrid.queryRadius(center, radius, options);
  }

  /**
   * Get entities within a rectangular area
   */
  getEntitiesInArea(bounds: Rectangle, options: QueryOptions = {}): Entity[] {
    return this.spatialGrid.queryRectangle(bounds, options);
  }

  /**
   * Clear all entities from the collision system
   */
  clear(): void {
    this.spatialGrid.clear();
  }

  /**
   * Mark the collision system as dirty, requiring rebuild on next access
   */
  markDirty(): void {
    this.isDirty = true;
  }

  /**
   * Check if the collision system needs rebuilding
   */
  isDirtyState(): boolean {
    return this.isDirty;
  }

  /**
   * Set the callback function to rebuild the collision system
   */
  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  /**
   * Ensure collision system is up to date before operations
   */
  private ensureUpToDate(): void {
    if (this.isDirty && this.rebuildCallback) {
      this.rebuildCallback();
      this.isDirty = false;
    }
  }

  /**
   * Get collision system statistics
   */
  getStats(): { spatialGrid: object; entityCount: number } {
    return {
      spatialGrid: this.spatialGrid.getStats(),
      entityCount: this.spatialGrid.getAllEntities().length,
    };
  }

  /**
   * Helper: Get all positions a building will occupy
   */
  private getBuildingPositions(
    building: Building,
    centerX: number,
    centerY: number
  ): Position[] {
    const positions: Position[] = [];
    const size = building.size || { width: 1, height: 1 };

    // Calculate offset from center to top-left corner
    const offsetX = Math.floor(size.width / 2);
    const offsetY = Math.floor(size.height / 2);

    for (let dy = 0; dy < size.height; dy++) {
      for (let dx = 0; dx < size.width; dx++) {
        positions.push({
          x: centerX - offsetX + dx,
          y: centerY - offsetY + dy,
        });
      }
    }

    return positions;
  }

  /**
   * Helper: Find collision rule between two layers
   */
  private findCollisionRule(
    layer1: CollisionLayer,
    layer2: CollisionLayer
  ): CollisionRule | undefined {
    return this.collisionRules.find(
      rule =>
        (rule.layer1 === layer1 && rule.layer2 === layer2) ||
        (rule.layer1 === layer2 && rule.layer2 === layer1)
    );
  }

  /**
   * Helper: Check if two entities can overlap based on collision rule
   */
  private canEntitiesOverlap(
    entity1: Entity,
    entity2: Entity,
    rule: CollisionRule
  ): boolean {
    if (rule.canOverlap) {
      return true;
    }

    // Make sure that the entities come in the same order as what the rule expects
    let firstEntity = entity1;
    let secondEntity = entity2;

    // Check if entities need to be swapped to match rule's expected order
    if (
      entity1.collisionLayer === rule.layer1 &&
      entity2.collisionLayer === rule.layer2
    ) {
      // Entities are already in correct order
      firstEntity = entity1;
      secondEntity = entity2;
    } else if (
      entity1.collisionLayer === rule.layer2 &&
      entity2.collisionLayer === rule.layer1
    ) {
      // Entities need to be swapped
      firstEntity = entity2;
      secondEntity = entity1;
    } else {
      // Neither entity matches the rule's expected layers - this should not happen
      throw new Error(
        `Entity layers don't match collision rule: entity1=${entity1.collisionLayer}, entity2=${entity2.collisionLayer}, rule=${rule.layer1}-${rule.layer2}`
      );
    }

    // Check condition if it exists
    if (rule.condition) {
      return !rule.condition(firstEntity, secondEntity);
    }

    return false;
  }

  /**
   * Helper: Validate building-specific placement rules
   */
  private validateBuildingSpecificRules(
    building: Building,
    x: number,
    y: number,
    errors: string[],
    _warnings: string[],
    _blockingEntities: Entity[]
  ): void {
    // Miners must be placed on resource nodes
    if (
      building.category === 'mining' &&
      building.requiresResourceNode !== false
    ) {
      const resourceNodes = this.spatialGrid.queryPosition(x, y, {
        layers: [CollisionLayer.RESOURCES],
      });

      if (resourceNodes.length === 0) {
        errors.push('Miners can only be placed on resource nodes');
      }
    }

    // Check world boundaries
    const size = building.size || { width: 1, height: 1 };
    if (
      x < 1 ||
      y < 1 ||
      x + size.width > GAME_CONFIG.WORLD_WIDTH - 1 ||
      y + size.height > GAME_CONFIG.WORLD_HEIGHT - 1
    ) {
      errors.push('Building extends outside world boundaries');
    }
  }
}
